if (!customElements.get('date-range-picker')) {
  customElements.define(
    'date-range-picker',
    class DateRangePicker extends HTMLElement {
      static MONTHS_TO_SHOW_DESKTOP = 2;
      static MONTHS_TO_SHOW_MOBILE = 1;
      static DESKTOP_BREAKPOINT = 750;
      static START_PROPERTY = 'Date de depart';
      static END_PROPERTY = 'Date de retour';

      connectedCallback() {
        this.periods = this.parsePeriods();
        if (!this.periods.length) return;

        this.mode = this.dataset.mode || 'product';
        this.formId = this.dataset.formId;
        this.lineKey = this.dataset.lineKey;
        this.selectedStart = this.parseDate(this.dataset.start);
        this.selectedEnd = this.parseDate(this.dataset.end);
        this.pendingEnd = Boolean(this.selectedStart && !this.selectedEnd);
        this.hoverDate = null;
        this.viewDate = this.getInitialViewDate();
        this.isExpanded = this.mode !== 'cart' || !(this.selectedStart && this.selectedEnd);

        if (this.mode === 'cart') {
          this.classList.add('date-range-picker--cart');
        }

        this.mount();
        this.bindEvents();

        if (this.mode === 'product') {
          this.waitForProductForm(() => {
            this.setupProductForm();
            this.syncProductForm();
          });
        }
      }

      waitForProductForm(callback) {
        const form = document.getElementById(this.formId);
        if (form) {
          callback(form);
          return;
        }

        const root = this.closest('product-info') || document.body;
        const observer = new MutationObserver(() => {
          const productForm = document.getElementById(this.formId);
          if (!productForm) return;
          observer.disconnect();
          callback(productForm);
        });

        observer.observe(root, { childList: true, subtree: true });
      }

      parsePeriods() {
        try {
          const periods = JSON.parse(this.dataset.periods || '[]');
          return Array.isArray(periods)
            ? periods
                .map((period) => ({
                  start: this.parseDate(period.start),
                  end: this.parseDate(period.end),
                }))
                .filter((period) => period.start && period.end)
            : [];
        } catch (error) {
          console.error(`date-range-picker: invalid periods JSON`, error);
          return [];
        }
      }

      parseDate(value) {
        if (!value) return null;
        const parts = String(value).split('-');
        if (parts.length !== 3) return null;
        const date = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
        return Number.isNaN(date.getTime()) ? null : date;
      }

      formatDate(date) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }

      datesEqual(a, b) {
        return a && b && this.formatDate(a) === this.formatDate(b);
      }

      getInitialViewDate() {
        if (this.selectedStart) {
          return new Date(this.selectedStart.getFullYear(), this.selectedStart.getMonth(), 1);
        }

        const firstPeriod = this.periods[0];
        if (firstPeriod?.start) {
          return new Date(firstPeriod.start.getFullYear(), firstPeriod.start.getMonth(), 1);
        }

        const today = new Date();
        return new Date(today.getFullYear(), today.getMonth(), 1);
      }

      isDateAllowed(date) {
        const time = date.getTime();
        return this.periods.some((period) => {
          return time >= period.start.getTime() && time <= period.end.getTime();
        });
      }

      isRangeAllowed(start, end) {
        const cursor = new Date(start);
        while (cursor.getTime() <= end.getTime()) {
          if (!this.isDateAllowed(cursor)) return false;
          cursor.setDate(cursor.getDate() + 1);
        }
        return true;
      }

      isValidEndDate(start, end) {
        if (!start || !end) return false;
        return end.getTime() >= start.getTime() && this.isRangeAllowed(start, end);
      }

      getMonthsToShow() {
        if (this.mode === 'cart') return 1;

        return window.innerWidth >= DateRangePicker.DESKTOP_BREAKPOINT
          ? DateRangePicker.MONTHS_TO_SHOW_DESKTOP
          : DateRangePicker.MONTHS_TO_SHOW_MOBILE;
      }

      getMonthDates(year, month) {
        const firstDay = new Date(year, month, 1);
        const startOffset = (firstDay.getDay() + 6) % 7;
        const gridStart = new Date(year, month, 1 - startOffset);

        return Array.from({ length: 42 }, (_, index) => {
          return new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + index);
        });
      }

      isInRange(date) {
        if (!this.selectedStart) return false;
        const time = date.getTime();
        const start = this.selectedStart.getTime();
        const end = (this.selectedEnd || this.hoverDate)?.getTime();
        if (!end) return this.datesEqual(date, this.selectedStart);
        const min = Math.min(start, end);
        const max = Math.max(start, end);
        return time >= min && time <= max;
      }

      getDayClassNames(date, monthIndex) {
        const classes = ['date-range-picker__day'];
        const inCurrentMonth = date.getMonth() === monthIndex;
        const allowed = this.isDateAllowed(date);

        if (!inCurrentMonth) classes.push('date-range-picker__day--outside');
        if (!allowed) classes.push('date-range-picker__day--disabled');
        if (this.datesEqual(date, this.selectedStart)) classes.push('date-range-picker__day--selected');
        if (this.datesEqual(date, this.selectedEnd)) classes.push('date-range-picker__day--selected');
        if (this.isInRange(date) && !this.datesEqual(date, this.selectedStart) && !this.datesEqual(date, this.selectedEnd)) {
          classes.push('date-range-picker__day--in-range');
        }
        if (this.pendingEnd && this.selectedStart && allowed && this.isValidEndDate(this.selectedStart, date)) {
          classes.push('date-range-picker__day--valid-end');
        }

        return classes.join(' ');
      }

      formatDisplayDate(date) {
        return new Intl.DateTimeFormat(document.documentElement.lang || 'fr', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        }).format(date);
      }

      mount() {
        this.innerHTML = `
          <div class="date-range-picker">
            <div class="date-range-picker__top">
              <p class="date-range-picker__steps caption" data-ref="steps"></p>
              <div class="date-range-picker__actions" data-ref="actions">
                <button type="button" class="date-range-picker__toggle link underlined-link hidden" data-ref="toggle">
                  Modifier les dates
                </button>
                <button type="button" class="date-range-picker__reset link underlined-link hidden" data-ref="reset">
                  Effacer
                </button>
              </div>
            </div>
            <div class="date-range-picker__summary" data-ref="summary"></div>
            <div class="date-range-picker__editor" data-ref="editor">
              <div class="date-range-picker__header">
                <button type="button" class="date-range-picker__nav button button--tertiary" data-action="prev" aria-label="Mois précédent">
                  <span aria-hidden="true">&#8249;</span>
                </button>
                <p class="date-range-picker__month-label caption-with-letter-spacing" data-ref="month-label"></p>
                <button type="button" class="date-range-picker__nav button button--tertiary" data-action="next" aria-label="Mois suivant">
                  <span aria-hidden="true">&#8250;</span>
                </button>
              </div>
              <p class="date-range-picker__message hidden" data-ref="message" role="alert"></p>
              <div class="date-range-picker__months" data-ref="months"></div>
            </div>
            <p class="date-range-picker__status visually-hidden" aria-live="polite" data-ref="status"></p>
          </div>
        `;

        this.stepsEl = this.querySelector('[data-ref="steps"]');
        this.toggleEl = this.querySelector('[data-ref="toggle"]');
        this.resetEl = this.querySelector('[data-ref="reset"]');
        this.editorEl = this.querySelector('[data-ref="editor"]');
        this.monthLabelEl = this.querySelector('[data-ref="month-label"]');
        this.summaryEl = this.querySelector('[data-ref="summary"]');
        this.messageEl = this.querySelector('[data-ref="message"]');
        this.monthsEl = this.querySelector('[data-ref="months"]');
        this.statusEl = this.querySelector('[data-ref="status"]');

        this.updateView();
      }

      renderMonth(monthDate) {
        const monthIndex = monthDate.getMonth();
        const year = monthDate.getFullYear();
        const monthLabel = monthDate.toLocaleDateString(document.documentElement.lang || 'fr', {
          month: 'long',
          year: 'numeric',
        });
        const weekdays = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];
        const dates = this.getMonthDates(year, monthIndex);

        const weekdayMarkup = weekdays
          .map((day) => `<th scope="col" class="date-range-picker__weekday">${day}</th>`)
          .join('');

        const rowMarkup = Array.from({ length: 6 }, (_, row) => {
          const cells = dates.slice(row * 7, row * 7 + 7).map((date) => {
            const disabled = !this.isDateAllowed(date);
            return `
              <td class="date-range-picker__cell">
                <button
                  type="button"
                  class="${this.getDayClassNames(date, monthIndex)}"
                  data-date="${this.formatDate(date)}"
                  ${disabled ? 'disabled' : ''}
                  aria-pressed="${this.datesEqual(date, this.selectedStart) || this.datesEqual(date, this.selectedEnd)}"
                  aria-label="${this.formatDisplayDate(date)}"
                >
                  ${date.getDate()}
                </button>
              </td>
            `;
          }).join('');
          return `<tr>${cells}</tr>`;
        }).join('');

        return `
          <div class="date-range-picker__month" data-month="${monthIndex}" data-year="${year}">
            ${this.mode !== 'cart' ? `<h3 class="date-range-picker__month-title caption-with-letter-spacing">${monthLabel}</h3>` : ''}
            <table class="date-range-picker__table" role="grid">
              <thead>
                <tr>${weekdayMarkup}</tr>
              </thead>
              <tbody>${rowMarkup}</tbody>
            </table>
          </div>
        `;
      }

      updateSummary() {
        if (this.mode === 'cart' && !this.isExpanded && this.selectedStart && this.selectedEnd) {
          this.summaryEl.innerHTML = `
            <div class="date-range-picker__cart-compact">
              <div class="date-range-picker__cart-dates">
                <span class="date-range-picker__cart-date">${this.formatDisplayDate(this.selectedStart)}</span>
                <span class="date-range-picker__cart-arrow" aria-hidden="true">→</span>
                <span class="date-range-picker__cart-date">${this.formatDisplayDate(this.selectedEnd)}</span>
              </div>
              <button type="button" class="date-range-picker__toggle button button--tertiary" data-ref="toggle">
                Modifier
              </button>
            </div>
          `;
          return;
        }

        if (this.selectedStart && this.selectedEnd) {
          this.summaryEl.innerHTML = `
            <div class="date-range-picker__selection date-range-picker__selection--complete">
              <div class="date-range-picker__selection-item">
                <span class="date-range-picker__selection-label">Départ</span>
                <strong>${this.formatDisplayDate(this.selectedStart)}</strong>
              </div>
              <div class="date-range-picker__selection-item">
                <span class="date-range-picker__selection-label">Retour</span>
                <strong>${this.formatDisplayDate(this.selectedEnd)}</strong>
              </div>
            </div>
          `;
          return;
        }

        if (this.selectedStart) {
          this.summaryEl.innerHTML = `
            <div class="date-range-picker__selection date-range-picker__selection--pending">
              <div class="date-range-picker__selection-item">
                <span class="date-range-picker__selection-label">Départ</span>
                <strong>${this.formatDisplayDate(this.selectedStart)}</strong>
              </div>
              <p class="date-range-picker__selection-hint caption">Choisissez maintenant la date de retour (dates surlignées).</p>
            </div>
          `;
          return;
        }

        this.summaryEl.innerHTML = `
          <p class="date-range-picker__selection-hint caption">
            Étape 1 : cliquez sur votre date de départ.
          </p>
        `;
      }

      updateSteps() {
        if (this.mode === 'cart') {
          this.stepsEl.innerHTML = '';
          this.stepsEl.classList.add('hidden');
          return;
        }

        this.stepsEl.classList.remove('hidden');
        const step = !this.selectedStart ? 1 : !this.selectedEnd ? 2 : 3;
        this.stepsEl.innerHTML = `
          <span class="date-range-picker__step${step === 1 ? ' date-range-picker__step--active' : ''}">1. Départ</span>
          <span class="date-range-picker__step${step === 2 ? ' date-range-picker__step--active' : ''}">2. Retour</span>
          <span class="date-range-picker__step${step === 3 ? ' date-range-picker__step--active' : ''}">3. Confirmé</span>
        `;
      }

      updateView() {
        const monthsToShow = this.getMonthsToShow();
        this.monthsEl.innerHTML = Array.from({ length: monthsToShow }, (_, index) => {
          const monthDate = new Date(this.viewDate.getFullYear(), this.viewDate.getMonth() + index, 1);
          return this.renderMonth(monthDate);
        }).join('');

        this.updateSummary();
        this.updateSteps();
        this.updateMonthLabel();
        this.updateCartLayout();
        this.resetEl.classList.toggle('hidden', !this.selectedStart || !this.isExpanded);
        this.clearMessage();
      }

      updateMonthLabel() {
        if (!this.monthLabelEl) return;

        this.monthLabelEl.textContent = this.viewDate.toLocaleDateString(document.documentElement.lang || 'fr', {
          month: 'long',
          year: 'numeric',
        });
      }

      updateCartLayout() {
        if (this.mode !== 'cart') {
          this.classList.remove('date-range-picker--collapsed');
          this.toggleEl?.classList.add('hidden');
          return;
        }

        const hasCompleteSelection = Boolean(this.selectedStart && this.selectedEnd);
        const isCollapsed = !this.isExpanded && hasCompleteSelection;

        this.classList.toggle('date-range-picker--collapsed', isCollapsed);
        this.toggleEl?.classList.toggle('hidden', true);
        this.stepsEl.classList.add('hidden');
        this.editorEl.classList.toggle('hidden', !this.isExpanded);
      }

      setExpanded(expanded) {
        this.isExpanded = expanded;
        if (expanded && this.selectedStart) {
          this.viewDate = new Date(this.selectedStart.getFullYear(), this.selectedStart.getMonth(), 1);
        }
        this.updateView();
      }

      bindEvents() {
        this.addEventListener('click', (event) => {
          const toggleButton = event.target.closest('[data-ref="toggle"]');
          if (toggleButton) {
            event.preventDefault();
            this.setExpanded(true);
            return;
          }

          const resetButton = event.target.closest('[data-ref="reset"]');
          if (resetButton) {
            event.preventDefault();
            this.clearSelection();
            this.setExpanded(true);
            return;
          }

          const navButton = event.target.closest('[data-action]');
          if (navButton) {
            event.preventDefault();
            this.navigate(navButton.dataset.action);
            return;
          }

          const dayButton = event.target.closest('[data-date]');
          if (!dayButton || dayButton.disabled) return;

          event.preventDefault();
          this.selectDate(this.parseDate(dayButton.dataset.date));
        });

        this.addEventListener('pointerover', (event) => {
          const dayButton = event.target.closest('[data-date]');
          if (!dayButton || dayButton.disabled || !this.pendingEnd || !this.selectedStart) return;

          const hoverDate = this.parseDate(dayButton.dataset.date);
          if (!this.isValidEndDate(this.selectedStart, hoverDate)) return;

          if (this.datesEqual(this.hoverDate, hoverDate)) return;
          this.hoverDate = hoverDate;
          this.refreshDayStyles();
        });

        this.addEventListener('pointerleave', (event) => {
          if (!this.contains(event.relatedTarget)) {
            this.hoverDate = null;
            if (this.pendingEnd) this.refreshDayStyles();
          }
        });

        this._resizeHandler = debounce(() => this.updateView(), 200);
        window.addEventListener('resize', this._resizeHandler);
      }

      disconnectedCallback() {
        window.removeEventListener('resize', this._resizeHandler);
      }

      refreshDayStyles() {
        this.querySelectorAll('[data-date]').forEach((button) => {
          const date = this.parseDate(button.dataset.date);
          const monthEl = button.closest('.date-range-picker__month');
          const monthIndex = Number(monthEl?.dataset.month ?? date.getMonth());

          button.className = this.getDayClassNames(date, monthIndex);
          button.setAttribute(
            'aria-pressed',
            this.datesEqual(date, this.selectedStart) || this.datesEqual(date, this.selectedEnd)
          );
        });
      }

      navigate(action) {
        const delta = action === 'next' ? 1 : -1;
        this.viewDate = new Date(this.viewDate.getFullYear(), this.viewDate.getMonth() + delta, 1);
        this.updateView();
      }

      clearSelection() {
        this.selectedStart = null;
        this.selectedEnd = null;
        this.pendingEnd = false;
        this.hoverDate = null;
        this.updateView();
        this.onSelectionChange();
      }

      selectDate(date) {
        if (!date || !this.isDateAllowed(date)) return;

        this.clearMessage();

        if (!this.selectedStart || this.selectedEnd || date.getTime() < this.selectedStart.getTime()) {
          this.selectedStart = date;
          this.selectedEnd = null;
          this.pendingEnd = true;
          this.hoverDate = null;
          this.updateView();
          this.onSelectionChange();
          this.announceStatus(`Date de départ sélectionnée : ${this.formatDisplayDate(date)}.`);
          return;
        }

        if (this.isValidEndDate(this.selectedStart, date)) {
          this.selectedEnd = date;
          this.pendingEnd = false;
          this.hoverDate = null;
          this.updateView();
          this.onSelectionChange();
          this.announceStatus(`Dates confirmées du ${this.formatDisplayDate(this.selectedStart)} au ${this.formatDisplayDate(this.selectedEnd)}.`);
          if (this.mode === 'cart') {
            this.isExpanded = false;
            this.updateCartLayout();
          }
          return;
        }

        this.showMessage('Cette plage traverse des dates indisponibles. Choisissez un retour dans la même période, sans saut.');
      }

      showMessage(message) {
        this.messageEl.textContent = message;
        this.messageEl.classList.remove('hidden');
      }

      clearMessage() {
        this.messageEl.textContent = '';
        this.messageEl.classList.add('hidden');
      }

      onSelectionChange() {
        if (this.mode === 'product') {
          this.syncProductForm();
          return;
        }

        if (this.mode === 'cart' && this.selectedStart && this.selectedEnd) {
          this.updateCartLine();
        }
      }

      setupProductForm() {
        if (this.startInput) return;

        const form = document.getElementById(this.formId);
        if (!form) return;

        this.startInput = document.createElement('input');
        this.startInput.type = 'hidden';
        this.startInput.name = `properties[${DateRangePicker.START_PROPERTY}]`;

        this.endInput = document.createElement('input');
        this.endInput.type = 'hidden';
        this.endInput.name = `properties[${DateRangePicker.END_PROPERTY}]`;

        form.appendChild(this.startInput);
        form.appendChild(this.endInput);

        this.submitButton = form.closest('product-form')?.querySelector('[type="submit"]');
      }

      syncProductForm() {
        if (!this.startInput || !this.endInput) return;

        const hasRange = this.selectedStart && this.selectedEnd;
        this.startInput.value = hasRange ? this.formatDate(this.selectedStart) : '';
        this.endInput.value = hasRange ? this.formatDate(this.selectedEnd) : '';

        if (this.submitButton && !this.submitButton.hasAttribute('disabled')) {
          if (hasRange) {
            this.submitButton.removeAttribute('aria-disabled');
          } else {
            this.submitButton.setAttribute('aria-disabled', 'true');
          }
        }
      }

      getCartSectionsToRender() {
        const sections = [];

        const mainCartItems = document.getElementById('main-cart-items');
        if (mainCartItems) {
          sections.push({
            id: 'main-cart-items',
            section: mainCartItems.dataset.id,
            selector: '.js-contents',
          });
        }

        sections.push({
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section',
        });

        sections.push({
          id: 'cart-live-region-text',
          section: 'cart-live-region-text',
          selector: '.shopify-section',
        });

        const mainCartFooter = document.getElementById('main-cart-footer');
        if (mainCartFooter) {
          sections.push({
            id: 'main-cart-footer',
            section: mainCartFooter.dataset.id,
            selector: '.js-contents',
          });
        }

        return sections;
      }

      getSectionInnerHTML(html, selector) {
        return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
      }

      parsePropertiesAttribute() {
        try {
          const raw = this.getAttribute('data-properties');
          if (!raw) return {};
          return this.normalizeLineItemProperties(JSON.parse(raw));
        } catch (error) {
          console.error(`date-range-picker: invalid properties JSON`, error);
          return {};
        }
      }

      normalizeLineItemProperties(raw) {
        if (!raw || typeof raw !== 'object') return {};

        if (Array.isArray(raw)) {
          return raw.reduce((properties, entry) => {
            if (Array.isArray(entry) && entry.length >= 2) {
              const name = entry[0];
              const value = entry[1];
              if (name != null && value != null && String(value).trim() !== '') {
                properties[String(name)] = String(value);
              }
              return properties;
            }

            if (entry && typeof entry === 'object' && entry.name != null) {
              if (entry.value != null && String(entry.value).trim() !== '') {
                properties[String(entry.name)] = String(entry.value);
              }
            }

            return properties;
          }, {});
        }

        const properties = {};
        Object.entries(raw).forEach(([key, value]) => {
          if (/^\d+$/.test(String(key))) return;
          if (value == null || String(value).trim() === '') return;

          if (Array.isArray(value) && value.length >= 2) {
            properties[String(value[0])] = String(value[1]);
            return;
          }

          properties[String(key)] = String(value);
        });

        return properties;
      }

      buildCartProperties() {
        const properties = this.parsePropertiesAttribute();

        delete properties[DateRangePicker.START_PROPERTY];
        delete properties[DateRangePicker.END_PROPERTY];

        if (this.selectedStart) {
          properties[DateRangePicker.START_PROPERTY] = this.formatDate(this.selectedStart);
        }
        if (this.selectedEnd) {
          properties[DateRangePicker.END_PROPERTY] = this.formatDate(this.selectedEnd);
        }

        return properties;
      }

      getLineQuantity() {
        const cartItem = this.closest('.cart-item, tr.cart-item');
        const quantityInput = cartItem?.querySelector('.quantity__input');
        return quantityInput ? parseInt(quantityInput.value, 10) : 1;
      }

      updateCartLine() {
        const cartItems = this.closest('cart-items');
        if (!cartItems || !this.lineKey) return;

        const properties = this.buildCartProperties();

        const sections = this.getCartSectionsToRender();
        const body = JSON.stringify({
          id: this.lineKey,
          quantity: this.getLineQuantity(),
          properties,
          sections: sections.map((section) => section.section),
          sections_url: window.location.pathname,
        });

        this.classList.add('date-range-picker--loading');

        fetch(`${routes.cart_change_url}`, { ...fetchConfig(), body })
          .then((response) => response.text())
          .then((state) => {
            const parsedState = JSON.parse(state);

            if (parsedState.errors) {
              this.showMessage(parsedState.errors);
              return;
            }

            sections.forEach((section) => {
              const container = document.getElementById(section.id);
              if (!container || !parsedState.sections?.[section.section]) return;
              const elementToReplace = container.querySelector(section.selector) || container;
              elementToReplace.innerHTML = this.getSectionInnerHTML(
                parsedState.sections[section.section],
                section.selector
              );
            });

            cartItems.classList.toggle('is-empty', parsedState.item_count === 0);

            const cartFooter = document.getElementById('main-cart-footer');
            if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);

            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'date-range-picker',
              cartData: parsedState,
            });

            this.announceStatus('Dates mises à jour.');
          })
          .catch((error) => {
            console.error(`date-range-picker: cart update failed`, error);
            this.showMessage('Impossible de mettre à jour les dates.');
          })
          .finally(() => {
            this.classList.remove('date-range-picker--loading');
          });
      }

      announceStatus(message) {
        if (this.statusEl) this.statusEl.textContent = message;
      }
    }
  );
}
